import { createSelector, createSlice } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";

const initialState = {};

const EMPTY_OBJECT = Object.freeze({});

const globalSlice = createSlice({
  name: "globalData",
  initialState,
  reducers: {
    setGlobalData: (state, action) => {
      return action.payload;
    },
  },
});

export const { setGlobalData } = globalSlice.actions;

export default globalSlice.reducer;

export const getGlobalData = (state) => state.globalData;

export const getProducts = createSelector(
  (state) => state.globalData,
  (globalData) => {
    if (globalData?.products) {
      return Object.values(globalData.products);
    }
    return [];
  }
);

export const getAppSettings = (state) => {
  const globalData = state.globalData;
  if (globalData?.app_settings) {
    return globalData.app_settings;
  }
  return {};
};

export const getConsolidatedStoneTypes = (state) => {
  const globalData = state.globalData;
  if (globalData?.consolidated_stone_types) {
    return globalData?.consolidated_stone_types;
  }
  return EMPTY_OBJECT;
};

export const globalScreens = (state) => {
  const globalData = state.globalData;
  if (globalData?.screens) {
    return globalData?.screens;
  }
  return {};
};

export const getOverlayConfig = (state) => {
  const globalData = state.globalData;
  if (
    globalData &&
    typeof globalData.overlay_config === "object" &&
    globalData.overlay_config !== null
  ) {
    return globalData.overlay_config;
  }
  return {};
};

export const getOverlayConfigWeb = (state) => {
  const globalData = state.globalData;
  if (
    globalData &&
    typeof globalData.overlay_config_web === "object" &&
    globalData.overlay_config_web !== null
  ) {
    return globalData.overlay_config_web;
  }
  return {};
};

export const getSEOConfig = (state) => {
  const globalData = state.globalData;
  if (
    globalData &&
    typeof globalData.seo_config === "object" &&
    globalData.seo_config !== null
  ) {
    return globalData.seo_config;
  }
  return {};
};

export const getFirebaseConfig = (state) => {
  const globalData = state.globalData;
  if (
    globalData &&
    typeof globalData.firebase_config === "object" &&
    globalData.firebase_config !== null
  ) {
    return globalData.firebase_config;
  }
  return {};
};

export const getEnvConfig = (state: any) => {
  const globalData = state.globalData;
  if (
    globalData &&
    typeof globalData?.env === "object" &&
    globalData?.env !== null
  ) {
    return globalData.env;
  }
  return {};
};

export const getShareOptionMenu = createSelector(
  (state) => state.globalData?.share_menu_options,
  (options) =>
    typeof options === "object" && options !== null ? options : initialState
);

export const getScreenWiseShareOptionMenu = (
  state: any,
  screenName: string
) => {
  const globalData = state.globalData;
  if (
    globalData &&
    typeof globalData?.[screenName]?.share_menu_options === "object" &&
    globalData?.[screenName]?.share_menu_options !== null
  ) {
    return globalData?.[screenName]?.share_menu_options;
  } else if (
    globalData &&
    typeof globalData?.share_menu_options === "object" &&
    globalData?.share_menu_options !== null
  ) {
    return globalData?.share_menu_options;
  }
  return {};
};

export const getFavoriteBulkAction = createSelector(
  (state) => state.globalData?.bulk_action?.options,
  (options) =>
    typeof options === "object" && options !== null ? options : initialState
);

export const getScreenWiseFavoriteBulkAction = (
  state: any,
  screenName: string
) => {
  const globalData = state.globalData;
  if (
    globalData &&
    typeof globalData?.[screenName]?.bulk_action === "object" &&
    globalData?.[screenName]?.bulk_action !== null
  ) {
    return globalData?.[screenName]?.bulk_action;
  } else if (
    globalData &&
    typeof globalData?.bulk_action?.options === "object" &&
    globalData?.bulk_action?.options !== null
  ) {
    return globalData?.bulk_action?.options;
  }
  return {};
};

export const getAllowedMaxQuantity = (state) => {
  const globalData = state.globalData;
  if (
    globalData &&
    typeof globalData.allow_max_quantity === "boolean" &&
    globalData.allow_max_quantity !== null
  ) {
    return globalData.allow_max_quantity;
  }
  return false;
};

const EMPTY_ORDER_CART = {};
export const getOrderCart = createSelector(
  (state) => state.globalData?.orderCart,
  (orderCart) => {
    if (typeof orderCart === "object" && orderCart !== null) {
      return orderCart;
    }
    return EMPTY_ORDER_CART;
  }
);

const EMPTY_SHARE_OPTION_PRICE_DISCLAIMER = {};
export const getShareOptionPriceDisclaimer = createSelector(
  (state) => state.globalData?.share_option_price_disclaimer,
  (shareOptionPriceDisclaimer) => {
    if (
      typeof shareOptionPriceDisclaimer === "object" &&
      shareOptionPriceDisclaimer !== null
    ) {
      return shareOptionPriceDisclaimer;
    }
    return EMPTY_SHARE_OPTION_PRICE_DISCLAIMER;
  }
);

export const getColumnCount = (state) => {
  const globalData = state.globalData;
  if (
    globalData &&
    typeof globalData?.item_card_detail_column_count === "object" &&
    globalData?.item_card_detail_column_count !== null
  ) {
    return globalData.item_card_detail_column_count;
  }
  return {};
};

export const getSkeleton = (state) => {
  const globalData = state.globalData;
  if (
    globalData &&
    typeof globalData.enable_skeleton === "boolean" &&
    globalData.enable_skeleton !== null
  ) {
    return globalData.enable_skeleton;
  }
  return false;
};

export const getEnableRequestItemFromSupplier = (state) => {
  const globalData = state.globalData;
  if (
    globalData &&
    typeof globalData?.app_settings?.enable_request_item_from_supplier ===
      "boolean" &&
    globalData?.app_settings?.enable_request_item_from_supplier !== null
  ) {
    return globalData?.app_settings?.enable_request_item_from_supplier;
  }
  return false;
};

export const getEnablePairShareOptions = (state) => {
  const globalData = state.globalData;
  if (
    globalData?.app_settings &&
    typeof globalData.app_settings.enable_pair_share_options === "boolean"
  ) {
    return globalData.app_settings.enable_pair_share_options;
  }
  return false;
};

export const getEnablePersonalisationFont = (state) => {
  const globalData = state.globalData;
  if (
    globalData?.app_settings &&
    typeof globalData.app_settings.enablePersonalisationFont === "boolean"
  ) {
    return globalData.app_settings.enablePersonalisationFont;
  }
  return false;
};

export const hideFiltersBasedOnAllowedStockItemsType = (state) => {
  const globalData = state.globalData;
  if (
    globalData?.app_settings &&
    typeof globalData.app_settings
      .hide_filters_based_on_allowed_stock_items_type === "boolean"
  ) {
    return globalData.app_settings
      ?.hide_filters_based_on_allowed_stock_items_type;
  }
  return false;
};

export const getSupplierSpecificCommentInOrangeMode = (state) => {
  const globalData = state.globalData;
  if (
    globalData?.app_settings &&
    typeof globalData.app_settings.show_supplier_comments_in_orange_mode ===
      "boolean"
  ) {
    return globalData.app_settings.show_supplier_comments_in_orange_mode;
  }
  return false;
};

export const getHideSummarySectionProduct = (state) => {
  const globalData = state.globalData;
  if (
    globalData &&
    typeof globalData.hide_summary_section_product === "object" &&
    globalData.hide_summary_section_product !== null
  ) {
    return globalData.hide_summary_section_product;
  }
  return {};
};

export const getAllowQuantityOnBulk = (state) => {
  const globalData = state.globalData;
  if (
    globalData?.app_settings &&
    typeof globalData.app_settings.allow_quantity_on_bulk === "boolean"
  ) {
    return globalData.app_settings?.allow_quantity_on_bulk;
  }
  return false;
};

export const getAllowJewelryVariationModal = (state) => {
  const globalData = state.globalData;
  if (
    globalData?.app_settings &&
    typeof globalData.app_settings.allow_jewelry_variation_modal === "boolean"
  ) {
    return globalData.app_settings?.allow_jewelry_variation_modal;
  }
  return false;
};

export const getAllowBMHAndCartActions = (state) => {
  const globalData = state.globalData;
  if (
    globalData?.app_settings &&
    typeof globalData.app_settings.allow_bmh_and_cart_actions === "boolean"
  ) {
    return globalData.app_settings.allow_bmh_and_cart_actions;
  }
  return false;
};

export const getAIChatBotConfig = (state) => {
  const globalData = state.globalData;
  if (
    globalData &&
    typeof globalData.ai_chat_bot === "object" &&
    globalData.ai_chat_bot !== null
  ) {
    return globalData.ai_chat_bot;
  }
  return EMPTY_OBJECT;
};

export const getVanityUrls = (state) => {
  const globalData = state.globalData;
  if (
    globalData &&
    typeof globalData.vanity_url_config === "object" &&
    globalData.vanity_url_config !== null
  ) {
    return globalData.vanity_url_config;
  }
  return {};
};

export const getHightLightThreeDotsMenu = (state) => {
  const globalData = state.globalData;
  if (
    globalData?.app_settings &&
    typeof globalData.app_settings.highlight_three_dot_menu === "boolean"
  ) {
    return globalData.app_settings.highlight_three_dot_menu;
  }
  return false;
};

export const getEnableProductSpecificFilterButtonColor = (state) => {
  const globalData = state.globalData;
  if (
    globalData?.app_settings &&
    typeof globalData.app_settings
      .enable_product_specific_filter_button_color === "boolean"
  ) {
    return globalData.app_settings.enable_product_specific_filter_button_color;
  }
  return false;
};

export const useProducts = () => {
  return useSelector(getProducts);
};
export const useGlobalData = () => {
  return useSelector(getGlobalData);
};

export const useAppSettings = () => {
  return useSelector(getAppSettings);
};

export const useOverlayConfig = () => {
  return useSelector(getOverlayConfig);
};

export const useOverlayConfigWeb = () => {
  return useSelector(getOverlayConfigWeb);
};

export const useGlobalScreens = () => {
  return useSelector(globalScreens);
};

export const useSEOConfig = () => {
  return useSelector(getSEOConfig);
};

export const useFirebaseConfig = () => {
  return useSelector(getFirebaseConfig);
};

export const useEnvConfig = () => {
  return useSelector(getEnvConfig);
};

export const useShareOptionConfig = () => {
  return useSelector(getShareOptionMenu);
};
export const useScreenWiseShareOptionConfig = (screenName: string) => {
  return useSelector((state) =>
    getScreenWiseShareOptionMenu(state, screenName)
  );
};
export const useAllowedMaxQuantity = () => {
  return useSelector(getAllowedMaxQuantity);
};

export const useOrderCart = () => {
  return useSelector(getOrderCart);
};

export const useShareOptionPriceDisclaimer = () => {
  return useSelector(getShareOptionPriceDisclaimer);
};

export const useGetSkeleton = () => {
  return useSelector(getSkeleton);
};

export const useEnableRequestItemFromSupplier = () => {
  return useSelector(getEnableRequestItemFromSupplier);
};

export const useEnablePairShareOptions = () => {
  return useSelector(getEnablePairShareOptions);
};

export const useEnablePersonalisationFont = () => {
  return useSelector(getEnablePersonalisationFont);
};

export const useColumnCount = () => {
  return useSelector(getColumnCount);
};

export const useHideFiltersBasedOnAllowedStockItemsType = () => {
  return useSelector(hideFiltersBasedOnAllowedStockItemsType);
};

export const useSupplierSpecificCommentInOrangeMode = () => {
  return useSelector(getSupplierSpecificCommentInOrangeMode);
};

export const getSearchApiDomain = (state: any) => {
  const globalData = state.globalData;
  const domain = globalData?.app_settings?.searchApiDomain;
  if (domain && typeof domain === "string") {
    return domain.endsWith("/") ? domain : domain + "/";
  }
  return undefined;
};

export const useSearchApiDomain = () => {
  return useSelector(getSearchApiDomain);
};

export const useFavoriteBulkAction = () => {
  return useSelector(getFavoriteBulkAction);
};

export const useHideSummarySectionProduct = () => {
  return useSelector(getHideSummarySectionProduct);
};

export const getEnableMobileWebView = (state) => {
  const globalData = state.globalData;
  if (
    globalData?.app_settings &&
    typeof globalData.app_settings.enable_mobile_web_view === "boolean"
  ) {
    return globalData.app_settings.enable_mobile_web_view;
  }
  return false;
};

export const useEnableMobileWebView = () => {
  return useSelector(getEnableMobileWebView);
};

export const useAIChatBotConfig = () => {
  return useSelector(getAIChatBotConfig);
};

export const useScreenWiseFavoriteBulkAction = (screenName: string) => {
  return useSelector((state) =>
    getScreenWiseFavoriteBulkAction(state, screenName)
  );
};

export const useAllowQuantityOnBulk = () => {
  return useSelector(getAllowQuantityOnBulk);
};

export const useAllowBMHAndCartActions = () => {
  return useSelector(getAllowBMHAndCartActions);
};

export const useVanityUrls = () => {
  return useSelector(getVanityUrls);
};

export const useAllowJewelryVariationModal = () => {
  return useSelector(getAllowJewelryVariationModal);
};

export const useConsolidatedStoneTypes = () => {
  return useSelector(getConsolidatedStoneTypes);
};

export const useEnableProductSpecificFilterButtonColor = () => {
  return useSelector(getEnableProductSpecificFilterButtonColor);
};
export const useHightLightThreeDotsMenu = () => {
  return useSelector(getHightLightThreeDotsMenu);
};
